import { TokenLogo } from "../components/TokenSelect";
import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { formatUnits } from "viem";
import { ExternalLink, Flag, ShieldCheck, ShieldQuestion, Globe, Copy } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, errMsg } from "../lib/api";
import { fmtUsd, fmtNum, fmtPct, short, isAddress, timeAgo } from "../lib/format";
import { tokenUrl, txUrl, addrUrl } from "../lib/chains";
import { useApp } from "../context/AppContext";
import { PriceChart } from "../components/PriceChart";
import { ChatWall } from "../components/ChatWall";
import { TopTraders } from "../components/TopTraders";
import { Panel, Loading, ErrorState, Empty } from "../components/State";
import { SwapCard } from "../components/SwapCard";
import { CompetitionBoard } from "../components/competition/CompetitionBoard";
import { OriginalBadge, useIsOriginal } from "../components/OriginalBadge";
import { AddToWallet, useTokenMetadata } from "../components/TokenVisibility";
import { TokenBanner, CustomizeMenu, themeStyle, fileUrl } from "../components/TokenPageCustomize";

const Fact = ({ k, v, testid }) => <div className="flex justify-between gap-4 py-2 border-b border-white/5 text-xs"><span className="text-slate-500">{k}</span><span data-testid={testid} className="font-mono text-slate-200 text-right break-all">{v}</span></div>;

const useBlockscoutVerified = (address) => {
  const [v, setV] = useState("checking");
  useEffect(() => {
    fetch(`${process.env.REACT_APP_RH_EXPLORER}/api/v2/smart-contracts/${address}`).then((r) => r.ok ? r.json() : Promise.reject())
      .then((d) => setV(d.is_verified ? "verified" : "unverified")).catch(() => setV("unknown"));
  }, [address]);
  return v;
};

const ReportDialog = ({ address, open, onOpenChange }) => {
  const [reason, setReason] = useState("scam");
  const [details, setDetails] = useState("");
  const submit = async () => {
    try { await api.post(`/tokens/${address}/report`, { reason, details }); toast.success("Report submitted to moderators"); onOpenChange(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#12161F] border-lime/20 text-slate-100" data-testid="report-token-dialog">
        <DialogHeader><DialogTitle>Report token</DialogTitle></DialogHeader>
        <select data-testid="report-reason-select" value={reason} onChange={(e) => setReason(e.target.value)} className="field">
          {["scam", "impersonation", "malicious_contract", "wrong_info", "other"].map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
        </select>
        <textarea data-testid="report-details-input" value={details} onChange={(e) => setDetails(e.target.value)} maxLength={1000} rows={4} placeholder="Details (optional)" className="field" />
        <button data-testid="report-submit-button" onClick={submit} className="btn-lime py-2.5 text-sm">Submit report</button>
      </DialogContent>
    </Dialog>
  );
};

const RecentTrades = ({ pool }) => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/market/pools/${pool}/trades`, { params: { limit: 25 } }).then((r) => setS(r.data.trades)).catch(() => setS([])); }, [pool]);
  if (s === null) return <Loading />;
  if (!s.length) return <Empty title="No recent indexed trades" />;
  return (
    <ul className="divide-y divide-white/5 text-xs" data-testid="token-recent-trades">
      {s.map((t) => (
        <li key={t.tx_hash + t.from} className="flex items-center gap-3 px-5 py-2">
          <span className={`w-9 font-bold uppercase ${t.kind === "buy" ? "text-lime" : "text-danger"}`}>{t.kind}</span>
          <span className="font-mono">{fmtUsd(t.volume_usd)}</span>
          <a href={addrUrl(t.from)} target="_blank" rel="noreferrer" className="font-mono text-slate-400 hover:text-lime">{short(t.from)}</a>
          <a href={txUrl(t.tx_hash)} target="_blank" rel="noreferrer" className="ml-auto text-slate-500 hover:text-lime flex items-center gap-1">{timeAgo(t.timestamp)} <ExternalLink className="h-3 w-3" /></a>
        </li>
      ))}
    </ul>
  );
};

const Stat = ({ k, v, tone = "text-white", testid }) => (
  <div className="rounded-xl border border-white/10 bg-black/30 px-4 py-3"><p className="text-[10px] uppercase tracking-widest text-slate-500">{k}</p><p className={`mt-1 font-mono text-base font-bold ${tone}`} data-testid={testid}>{v}</p></div>
);

const TokenStats = ({ market, address }) => {
  const pools = market?.pools || [];
  const a = address.toLowerCase();
  const best = pools.reduce((x, p) => (!x || (p.liquidity_usd || 0) > (x.liquidity_usd || 0) ? p : x), null);
  const base = best && best.base_token?.address?.toLowerCase() === a;
  const price = best ? (base ? best.price_usd : best.quote_price_usd) : null;
  const ch = base ? best.change_24h_pct : null;
  const mc = base ? best.market_cap_usd ?? best.fdv_usd : null;
  const sum = (k) => (pools.length ? pools.reduce((s, p) => s + (p[k] || 0), 0) : null);
  return (
    <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="token-stats">
      <Stat k="Price" v={market ? fmtUsd(price) : "…"} tone="text-lime" testid="token-stat-price" />
      <Stat k={base && best.market_cap_usd == null ? "FDV" : "Market cap"} v={market ? fmtUsd(mc) : "…"} testid="token-stat-mcap" />
      <Stat k="Liquidity" v={market ? fmtUsd(sum("liquidity_usd")) : "…"} testid="token-stat-liquidity" />
      <Stat k="Volume 24h" v={market ? fmtUsd(sum("volume_24h_usd")) : "…"} testid="token-stat-volume" />
      <Stat k="24h change" v={fmtPct(ch)} tone={ch == null ? "text-slate-500" : ch >= 0 ? "text-lime" : "text-red-400"} testid="token-stat-change" />
    </div>
  );
};

const MetaButtons = ({ address }) => {
  const m = useTokenMetadata(address);
  if (!m) return null;
  return (<>
    <AddToWallet meta={m} testid="token-add-to-metamask" />
    <a href={m.hivevora.metadata} target="_blank" rel="noreferrer" data-testid="token-metadata-link" className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1 text-[11px] text-slate-300 hover:text-lime">Metadata <ExternalLink className="h-3 w-3" /></a>
  </>);
};

export default function TokenProfile() {
  const { address } = useParams();
  const [sp] = useSearchParams();
  const { user } = useApp();
  const [chainData, setChainData] = useState(null);
  const [err, setErr] = useState(null);
  const [market, setMarket] = useState(null);
  const [listing, setListing] = useState(undefined);
  const [pool, setPool] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [page, setPage] = useState(null);
  const [baseLogos, setBaseLogos] = useState({});
  const isOriginal = useIsOriginal(address);
  useEffect(() => { if (window.location.hash === "#swap" && chainData) setTimeout(() => document.getElementById("swap")?.scrollIntoView({ behavior: "smooth", block: "start" }), 300); }, [chainData]);
  const verified = useBlockscoutVerified(address);

  useEffect(() => {
    if (!isAddress(address)) return setErr("Invalid contract address");
    api.get(`/chain/token/${address}`).then((r) => setChainData(r.data)).catch((e) => setErr(errMsg(e)));
    api.get(`/market/tokens/${address}`).then((r) => { setMarket(r.data); setPool(r.data.pools[0]?.address || null); }).catch(() => setMarket({ pools: [], info: null }));
    api.get(`/listings/by-address/${address}`).then((r) => setListing(r.data)).catch(() => setListing(null));
    api.get("/tokens/base-logos").then((r) => setBaseLogos(r.data)).catch(() => {});
  }, [address]);
  useEffect(() => {
    if (isAddress(address)) api.get(`/token-page/${address}`).then((r) => setPage(r.data)).catch(() => setPage({ theme: {} }));
  }, [address, user]);

  if (err) return <div className="max-w-3xl mx-auto py-20"><ErrorState message={err} testid="token-profile-error" /></div>;
  if (!chainData) return <Loading label="Reading token contract on Robinhood Chain" />;
  const m = listing?.metadata;
  const logo = fileUrl(page?.logo_url || m?.logo_url) || market?.info?.image_url || baseLogos[address.toLowerCase()];
  const tradable = !!listing || !!page?.hive_launch || isOriginal;
  const outToken = { address: address.toLowerCase(), symbol: chainData.symbol, name: chainData.name, decimals: chainData.decimals, logo, verified: false };
  return (
    <div className={`mx-auto max-w-[1600px] px-4 lg:px-8 py-8 ${themeStyle(page?.theme) ? "tp-themed" : ""}`} style={themeStyle(page?.theme)} data-testid="token-profile-page">
      <TokenBanner page={page} symbol={chainData.symbol} />
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <TokenLogo t={{ symbol: chainData.symbol, address, logo, logo_fallback: page?.logo_fallback }} size={56} />
        <div>
          <h1 data-testid="token-profile-title" className="font-display text-3xl sm:text-4xl font-black text-white flex flex-wrap items-center gap-2">{chainData.symbol} <OriginalBadge address={address} large /> <span className="text-lg text-slate-400 font-sans font-medium">{chainData.name}</span></h1>
          <span className="flex items-center gap-2"><a href={tokenUrl(address)} target="_blank" rel="noreferrer" className="text-xs font-mono text-slate-400 hover:text-lime flex items-center gap-1 break-all">{address} <ExternalLink className="h-3 w-3 shrink-0" /></a><button data-testid="token-copy-address" onClick={() => navigator.clipboard.writeText(address).then(() => toast.success("Contract address copied"))} className="text-slate-500 hover:text-lime"><Copy className="h-3.5 w-3.5" /></button></span>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <span data-testid="token-listing-status" className={`rounded-full px-3 py-1 text-[11px] font-bold ${listing ? "bg-lime/15 text-lime" : "bg-white/5 text-slate-400"}`}>{listing ? (page?.hive_launch ? "HIVEVORA LAUNCH · auto-listed" : "HIVEVORA LISTED · reviewed metadata") : "NOT LISTED ON HIVEVORA"}</span>
          <MetaButtons address={address} />
          {page?.can_edit && <CustomizeMenu key={address} address={address} page={page} onChange={setPage} defaultOpen={sp.get("customize") === "1"} />}
          <Link to={`/liquidity?tab=add&token=${address}`} data-testid="token-add-liquidity" className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1 text-[11px] text-slate-300 hover:text-lime hover:border-lime/40">+ Liquidity</Link>
          <button data-testid="token-report-button" onClick={() => user ? setReportOpen(true) : toast.error("Sign in with your wallet to report")} className="flex items-center gap-1 rounded-full border border-danger/40 px-3 py-1 text-[11px] text-danger hover:bg-danger/10"><Flag className="h-3 w-3" /> Report</button>
        </div>
      </div>
      <TokenStats market={market} address={address} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
        <div className="order-1 lg:col-span-8 min-w-0 space-y-5" data-testid="token-left-column">
          {pool ? <PriceChart pool={pool} onPoolChange={setPool} hiveConfigured /> : market?.pools_error ? <Panel title="Chart"><ErrorState message="Market data provider is rate-limited — reload in a minute." /></Panel> : <Panel title="Chart"><Empty title="No indexed trading pool for this token" hint="No price is shown because no reliable market data exists." /></Panel>}
        </div>
        <aside className="order-2 lg:col-span-4 lg:row-span-2 min-w-0 lg:sticky lg:top-20 space-y-5" data-testid="token-swap-column">
          {tradable ? <div id="swap" className="scroll-mt-24 rounded-2xl border border-lime/30 bg-[#0b0d11]/95 shadow-[0_0_24px_rgba(57,255,20,0.08)]" data-testid="token-page-swap"><SwapCard key={address} defaultOut={outToken} sourceToken={address} /></div>
            : <Panel title="Trade" testid="token-swap-unavailable"><div className="p-5 text-sm text-slate-400">Swaps on HIVEVORA are available for tokens launched or listed here. <Link to="/listings" className="text-lime underline">List this token</Link> to enable trading on its profile.</div></Panel>}
          <Panel title="On-chain facts (verified by HIVEVORA backend)" testid="token-onchain-facts">
            <div className="px-5 py-3">
              <Fact k="Decimals" v={chainData.decimals} />
              <Fact k="Total supply" v={fmtNum(formatUnits(BigInt(chainData.total_supply_raw), chainData.decimals), 2)} testid="token-total-supply" />
              <Fact k="Bytecode size" v={`${chainData.bytecode_size} bytes`} />
              <Fact k="Source verification" testid="token-verification" v={<span className="flex items-center gap-1 justify-end">{verified === "verified" ? <ShieldCheck className="h-3.5 w-3.5 text-lime" /> : <ShieldQuestion className="h-3.5 w-3.5 text-warning" />}{verified === "unknown" ? "check on Blockscout" : verified}</span>} />
              <Fact k="Holders" v={market?.info?.holders?.count ? fmtNum(market.info.holders.count) : "unavailable"} />
              <Fact k="Provider score (GT)" v={market?.info?.gt_score ? Number(market.info.gt_score).toFixed(1) : "—"} />
            </div>
          </Panel>
        </aside>
        <div className="order-3 lg:col-span-8 min-w-0 space-y-5">
          <CompetitionBoard address={address} />
          <Panel title="Project info (submitted by project)">
            <div className="px-5 py-4 text-sm text-slate-300 space-y-3">
              {m ? (<>
                <p className="leading-relaxed">{m.description}</p>
                <div className="flex flex-wrap gap-2 text-xs">{["website", "twitter", "telegram"].filter((k) => m[k]).map((k) => <a key={k} href={m[k]} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-1 rounded bg-white/5 px-2 py-1 hover:text-lime"><Globe className="h-3 w-3" />{k}</a>)}</div>
              </>) : <p className="text-xs text-slate-500">This token has no HIVEVORA listing. {market?.info?.description ? `Provider description: ${market.info.description.slice(0, 300)}` : ""}</p>}
              <p className="text-[10px] text-slate-600">Project-submitted metadata is stored separately from on-chain data and is not verified for accuracy. Listing ≠ audit or endorsement.</p>
            </div>
          </Panel>
          <Panel title="Trading pairs & routes">
            {!market ? <Loading /> : market.pools_error ? <ErrorState testid="token-pools-error" message={`Market data temporarily unavailable — ${market.pools_error}`} /> : !market.pools.length ? <Empty title="No pools found" /> : (
              <ul className="divide-y divide-white/5" data-testid="token-pools-list">
                {market.pools.map((p) => (
                  <li key={p.address}><button onClick={() => setPool(p.address)} className={`w-full flex flex-wrap justify-between gap-3 px-5 py-2.5 text-xs text-left hover:bg-lime/5 ${pool === p.address ? "text-lime" : "text-slate-300"}`}>
                    <span className="font-semibold">{p.name}</span><span className="font-mono text-slate-500">{p.dex}</span>
                    <span className="font-mono">liq {fmtUsd(p.liquidity_usd)}</span><span className="font-mono">vol {fmtUsd(p.volume_24h_usd)}</span>
                    <span className={`font-mono ${/^uniswap-v[234]/.test(p.dex) ? "text-lime" : "text-slate-500"}`}>{/^uniswap-v[234]/.test(p.dex) ? "Routable via Uniswap" : "Not routed by HIVEVORA"}</span>
                  </button></li>
                ))}
              </ul>)}
          </Panel>
          {pool && <Panel title="Recent transactions"><RecentTrades pool={pool} /></Panel>}
        </div>
      </div>
      <section className="mt-8 border-t border-white/10 pt-6" data-testid="token-chat-section">
        <h2 className="mb-3 font-display text-xl font-black text-white">{chainData.symbol} community</h2>
        <ChatWall channels={[`token-${address.toLowerCase()}`, "general"]} initial={`token-${address.toLowerCase()}`} bgLogo={logo} />
      </section>
      {pool && <div className="mt-5" data-testid="token-top-traders"><TopTraders pool={pool} compact={false} /></div>}
      <ReportDialog address={address} open={reportOpen} onOpenChange={setReportOpen} />
    </div>
  );
}
